"use strict";

import React, {useEffect, useState} from 'react';
import FormRequiredLegend from "../../component/FormRequiredLegend";
import {Controller, useForm} from "react-hook-form";
import FormSelect from "../../component/FormSelect";
import FormSelectState from "../../component/FormSelectState";
import Icon from "../../component/Icon";
import formInputErrorClassName from "../../formInputErrorClassName";
import Cleave from "cleave.js/react";
import {validate as validateEmail} from "email-validator";
import validateRoutingNumber from "../../validateRoutingNumber";
import axios from "../utilities/axiosEcheckWrapper";
import {useAlert} from "react-alert";
import AccountAddressFormPhoneInput from "../../account/component/AccountAddressFormPhoneInput";

const AccountEchecksCreateFinancialAccountForm = ({data={}, onSubmit=()=>{},}) => {
    const {
        register,
        handleSubmit,
        control,
        reset,
        getValues,
        setValue,
        setError,
        formState: { errors }
    } = useForm();
    const [isFetchingPreScreenReport, setIsFetchingPreScreenReport] = useState(false);
    const alert = useAlert();

    useEffect(()=>{
        reset({...getValues(),...data} );
    }, [data, reset, getValues]);

    function fetchPreScreenReport(data={}){
        return new Promise((resolve, reject) => {
            setIsFetchingPreScreenReport(()=>{return true;});
            axios.post(`echecks/financial-links/accounts/report`, data)
                .then((response)=>{
                    resolve( response.data );
                })
                .catch((error)=>{
                    reject(error);
                }).finally(()=>{
                setIsFetchingPreScreenReport(()=>{return false;});
            });
        });
    }

    function handleFormSubmit(formData={}){
        fetchPreScreenReport(formData).then((response)=>{
            const preScreenReportResponse = response;
            if(preScreenReportResponse && Object.keys(preScreenReportResponse).length > 0){
                const decisionObj = preScreenReportResponse?.RESPONSE?.CONTENT?.DECISION || {};
                let valid = false;
                if(!!decisionObj){
                    const decisionCode = decisionObj?.decision?.code || '';
                    const decisionProperties = decisionObj?.PROPERTIES || [];
                    valid = decisionCode === 'A' || decisionCode === 'W';
                    if(!!decisionProperties && decisionProperties.length){
                        decisionProperties.forEach((property)=>{
                            if(property.name === 'messageStr'){
                                const errorMessage = property?.Value || '';
                                if(errorMessage.match(/.*Routing Number.*/ig)){
                                    valid = false;
                                    setError("bank_routing_number",
                                        { type: "invalidRoutingNumber", message: "Invalid Routing Number", },
                                        { shouldFocus: true }
                                    );
                                }
                                else if(errorMessage.match(/.*Account Structure.*/ig)){
                                    valid = false;
                                    setError("bank_account_number",
                                        { type: "invalidAccountNumber", message: "Invalid Account Number", },
                                        { shouldFocus: true }
                                    );
                                }
                                else if(!valid && !!errorMessage){
                                    alert.error(errorMessage);
                                }
                            }
                        });
                    }
                }
                if(valid)
                    onSubmit({...formData, preScreenReport: {...preScreenReportResponse}});
            }
        }).catch((e)=>{
            alert.error('Sorry, we are not able to verify your financial information at the moment. Please try again later.');
        });
    }

    function formInputClassName(name = '') {
        return formInputErrorClassName(name, errors);
    }

    return (
        <div style={{position: 'relative',}}>
            {isFetchingPreScreenReport &&
                <div className="loading loading--cover loading--center-flex loading--w-flip" />
            }
            <form id="authorization-create" name="authorization-create" className="is-marginless" noValidate={true}
                  onSubmit={handleSubmit(handleFormSubmit)}>
                <FormRequiredLegend/>
                <fieldset>
                    <legend>Financial Account Information:</legend>
                    <ol className="form__input-list">

                        <li className="form__input-list-item">
                            <label htmlFor="account-form--bank-account-type"
                                   className="form__label form__label--required">Account Type:</label>
                            <Controller
                                name="bank_account_type"
                                control={control}
                                rules={{required: true}}
                                render={({field: { onChange, onBlur, value, },}) =>
                                    <FormSelect name="bank_account_type" value={value}
                                                onChange={onChange} onBlur={onBlur} options={[
                                        {value: '', label: '',},
                                        {value: 'Checking', label: 'Checking Account',},
                                        {value: 'Savings', label: 'Savings Account',},
                                    ]}/>
                                }
                            />
                            {!!errors.bank_account_type &&
                                <small className="form__error">
                                    Please select the type of account this is
                                </small>
                            }
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--bank-routing-number"
                                   className="form__label form__label--required">
                                Financial Institution Routing Number:
                            </label>
                            <Controller
                                name="bank_routing_number"
                                control={control}
                                rules={{
                                    required: true,
                                    maxLength: 9,
                                    minLength: 9,validate: {
                                        isRoutingNumber: (value)=>{
                                            return validateRoutingNumber(value);
                                        }},
                                }}
                                render={({field: { onChange, onBlur, value,ref },}) =>
                                    <Cleave id="account-form--bank-routing-number"
                                            name="bank_routing_number"
                                            value={value}
                                            options={{numericOnly: true, blocks: [9]}}
                                            onBlur={onBlur}
                                            onChange={onChange}
                                            htmlRef={ref}
                                            className={formInputClassName('bank_routing_number')} />
                                }
                            />
                            {!!errors.bank_routing_number &&
                                <small className="form__error">
                                    A valid routing number is required
                                </small>
                            }
                            <div>
                                <img src="/assets/img/echeck/help-routing-number.png" alt="Routing Number Display" />
                            </div>
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--bank-account-number"
                                   className="form__label form__label--required">
                                Financial Institution Account Number:
                            </label>
                            <Controller
                                name="bank_account_number"
                                control={control}
                                rules={{required: true,
                                    maxLength: 17,
                                    minLength: 2,}}
                                render={({field: { onChange, onBlur, value,ref },}) =>
                                    <Cleave id="account-form--bank-account-number"
                                            name="bank_account_number"
                                            value={value}
                                            options={{numericOnly: true, blocks: [17],}}
                                            onBlur={onBlur}
                                            onChange={onChange}
                                            htmlRef={ref}
                                            className={formInputClassName('bank_account_number')} />
                                }
                            />
                            {!!errors.bank_account_number &&
                                <small className="form__error">
                                    A valid account number is required
                                </small>
                            }
                            <div>
                                <img src="/assets/img/echeck/help-account-number.png" alt="Account Number Display" />
                            </div>
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--company_name" className="form__label">
                                Company Name on Account:
                            </label>
                            <input type="text"
                                   id="account-form--company_name"
                                   autoComplete="off"
                                   maxLength={50}
                                   className={formInputClassName('company_name')}
                                   {...register('company_name')} />
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--f_name" className="form__label form__label--required">
                                First Name on Account:
                            </label>
                            <input type="text"
                                   id="account-form--f_name"
                                   autoComplete="off"
                                   maxLength={50}
                                   className={formInputClassName('f_name')}
                                   {...register('f_name', {required: true,})} />
                            {!!errors.f_name &&
                                <small className="form__error">
                                    You must provide a first name associated with your bank account
                                </small>
                            }
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--l_name" className="form__label form__label--required">
                                Last Name on Account:
                            </label>
                            <input type="text"
                                   id="account-form--l_name"
                                   autoComplete="off"
                                   maxLength={50}
                                   className={formInputClassName('l_name')}
                                   {...register('l_name', {required: true,})} />
                            {!!errors.l_name &&
                                <small className="form__error">
                                    You must provide a last name associated with your bank account
                                </small>
                            }
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--email"
                                   className="label__required">
                                Email:
                            </label>
                            <input type="email"
                                   {...register('email', {
                                       required: true,
                                       maxLength: 50,
                                       validate: {
                                           validateEmail: (value)=>{
                                               return validateEmail(value) || 'invalid email address';
                                           },
                                       },
                                   })}
                                   id="account-form--email"
                                   className={formInputClassName('email')} />
                            {!!errors.email &&
                                <small className="form__error">A valid email address is required</small>
                            }
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--phone"
                                   className="form__label form__label--required">
                                Phone:
                            </label>
                            <AccountAddressFormPhoneInput control={control}
                                                          setValue={setValue}
                                                          errors={errors}
                                                          inputClassName={formInputClassName('phone')} />
                            {!!errors.phone &&
                                <small className="form__error">
                                    A valid phone number is required
                                </small>
                            }
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--address"
                                   className="form__label form__label--required">
                                Address:
                            </label>
                            <input type="text"
                                   {...register('address', {required: true, })}
                                   id="account-form--address"
                                   maxLength="50"
                                   className={formInputClassName('address')}/>
                            {!!errors.address && errors.address.type === 'required' &&
                                <small className="form__error">Address is required</small>
                            }
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--city"
                                   className="form__label form__label--required">
                                City:
                            </label>
                            <input type="text"
                                   {...register('city', {required: true, maxLength: 40,})}
                                   id="account-form--city"
                                   maxLength="40"
                                   className={formInputClassName('city')}/>
                            {!!errors.city &&
                                <small className="form__error">
                                    City is required
                                </small>
                            }
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--state"
                                   className="form__label form__label--required">
                                State:
                            </label>
                            <Controller
                                name="state"
                                control={control}
                                rules={{required: true}}
                                render={({field: { onChange, onBlur, value, },}) =>
                                    <FormSelectState name="state"
                                                     id="account-form--state"
                                                     value={value}
                                                     className={formInputClassName('state')}
                                                     onChange={onChange}
                                                     onBlur={onBlur} />
                                }
                            />
                            {!!errors.state &&
                                <small className="form__error">
                                    State is required
                                </small>
                            }
                        </li>
                        <li className="form__input-list-item">
                            <label htmlFor="account-form--zip"
                                   className="form__label form__label--required">
                                Zip:
                            </label>
                            <Controller
                                name="zip"
                                control={control}
                                rules={{required: true,
                                    maxLength: 5,
                                    minLength: 5,}}
                                render={({field: { onChange, onBlur, value, ref},}) =>
                                    <Cleave id="account-form--zip"
                                            name="zip"
                                            value={value}
                                            options={{numericOnly: true,blocks: [5],}}
                                            onBlur={onBlur}
                                            onChange={onChange}
                                            htmlRef={ref}
                                            className={formInputClassName('zip')} />
                                }
                            />
                            {!!errors.zip &&
                                <small className="form__error">
                                    A valid zip code is required
                                </small>
                            }
                        </li>
                    </ol>
                </fieldset>
                <fieldset className="submit">
                    <button type="submit"
                            name="echeck__account__details"
                            className={`btn__primary no-wrap${
                                isFetchingPreScreenReport
                                    ? ' btn--disabled'
                                    : ''
                            }`}
                            disabled={isFetchingPreScreenReport} >
                        Next <Icon name="right" className="icon--collapse-right" />
                    </button>
                </fieldset>
            </form>
        </div>
    );
};

export default AccountEchecksCreateFinancialAccountForm;
